import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { ReactComponent as MoveLocationIcon } from '@assets/svg/ic-move-location.svg';

import ModalHeader from '@components/modals/ModalHeader/ModalHeader';
import Button from '@components/uikit/Button/Button';
import TreeSelect from '@components/uikit/TreeSelect/TreeSelect';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useLocationsGetOneQuery } from '@services/useLocationsGetOneQuery';
import { useLocationsSaveMutation } from '@services/useLocationsSaveMutation';

import { useModalActions } from '@store/useModalStore';

import {
  MOVE_LOCATION_FIELDS,
  moveLocationDefaultValue,
  schemaMoveLocation,
} from './constants/schemaMoveLocation';
import style from './style.module.scss';

const propTypes = {
  locationData: PropTypes.object,
  onClose: PropTypes.func,
};

const MoveLocationModal = ({ locationId, locationName }) => {
  const { t } = useTranslation();
  const { setModalClose } = useModalActions();

  const { data: dataOneLocation } = useLocationsGetOneQuery({ locationId });
  const { data: assetTreeData } = useAssetTreeQuery();

  const { control, handleSubmit, reset, setError } = useForm({
    mode: 'all',
    resolver: schemaMoveLocation(dataOneLocation?.parent.id),
    defaultValues: moveLocationDefaultValue(dataOneLocation?.parent.id),
  });

  const treeData = useMemo(() => {
    if (!dataOneLocation) {
      return [];
    }

    const getChildren = (children) => {
      if (!children.length) {
        return [];
      }
      return children.map((loc) => ({
        ...loc,
        disabled: loc.id === locationId,
        children: loc.id === locationId ? [] : getChildren(loc.children),
      }));
    };

    return assetTreeData.facilities.reduce((acc, loc) => {
      if (loc.facilityId === dataOneLocation.facility_id) {
        return acc.concat({
          ...loc,
          children: getChildren(loc.children),
        });
      }
      return acc;
    }, []);
  });

  const { mutate: locationsSaveMutate, isLoading: locationsSaveIsLoading } = useLocationsSaveMutation();

  const submit = handleSubmit((formData) => {
    locationsSaveMutate(
      {
        id: locationId,
        name: locationName,
        parent_id: formData.newParentId,
        parentId: formData.newParentId,
      },
      {
        onSuccess: () => {
          showToast({ variant: 'success', message: t('The location has been moved successfully.') });
          closeModal();
        },
        onError: (err) => {
          if (err?.response?.status === 422) {
            setError('newParentId', { message: err?.response?.data?.errors?.name });
          }
        },
      },
    );
  });

  const closeModal = () => {
    setModalClose();
    reset();
  };

  return (
    <div className={style.modalMoveLocation}>
      <ModalHeader />
      <div className={style.modalBody}>
        <div className={style.modalBodyTitle}>
          {t('Move Location')} <br />"{locationName}"
        </div>
        <div className={style.modalBodyForm}>
          <MoveLocationIcon className={style.moveIcon} />
          <TreeSelect
            label={t('Select A Facility To Move')}
            name={MOVE_LOCATION_FIELDS.NEW_PARENT_ID}
            defaultSelectedName={dataOneLocation?.parent.name || ''}
            control={control}
            required
            items={treeData}
            fieldNames={{
              children: 'children',
              title: 'name',
              key: 'id',
            }}
            searchable
            placeholder={t('Start To Type...')}
            defaultExpandAll
            type={'with-error'}
          />
        </div>
      </div>
      <div className={style.modalFooter}>
        <Button type="primary" text={t('Save')} disabled={locationsSaveIsLoading} onClick={submit} />
        <Button type="secondary" text={t('Cancel')} disabled={locationsSaveIsLoading} onClick={closeModal} />
      </div>
    </div>
  );
};

MoveLocationModal.propTypes = propTypes;

export default MoveLocationModal;
